// Chunked file upload (broadcast attachments). The web server in front of the
// app only accepts ~1 MB per request, so a file is never sent in one go:
// POST /broadcasts/uploads opens an upload, then each 700 KB slice is PUT as
// raw bytes. Big phone photos are shrunk first so they upload faster.
import { api } from "@/lib/api";

export const CHUNK_SIZE = 700000;
const SHRINK_OVER_BYTES = 700 * 1024;
const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.8;
const RETRIES = 3;              // extra tries per slice after the first one fails
const CHUNK_TIMEOUT_MS = 120000; // a 700 KB slice on a weak mobile signal can be slow

// Some phones/browsers give no type (or octet-stream) for picked files.
const EXT_TYPES = {
  ".pdf": "application/pdf",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".webp": "image/webp",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xls": "application/vnd.ms-excel",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".doc": "application/msword",
};

// Files already passed through shrinkImage — never re-encode a photo twice.
const prepared = new WeakSet();

export function extOf(name) {
  const i = (name || "").lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

export function contentTypeOf(file) {
  const t = (file?.type || "").toLowerCase();
  if (t && t !== "application/octet-stream") return t;
  return EXT_TYPES[extOf(file?.name)] || "application/octet-stream";
}

// JPEG/PNG/WEBP over 700 KB -> at most 1600 px on the long side, JPEG ~0.8.
// Returns the original file if it is small, not a photo, or would not shrink.
export async function shrinkImage(file) {
  if (prepared.has(file)) return file;
  if (!/^image\/(jpeg|png|webp)$/.test(contentTypeOf(file)) || file.size <= SHRINK_OVER_BYTES) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    const ctx = canvas.getContext("2d");
    // A transparent PNG would turn black in a JPEG — paint white underneath.
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    if (bmp.close) bmp.close();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", JPEG_QUALITY));
    if (!blob || blob.size >= file.size) { prepared.add(file); return file; }
    const out = new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg",
      { type: "image/jpeg", lastModified: Date.now() });
    prepared.add(out);
    return out;
  } catch {
    return file;
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Network drops, timeouts and server hiccups are worth retrying; a 4xx
// (wrong file type, upload not found, not yours) will not get better.
function isRetryable(err) {
  const status = err?.response?.status;
  return !status || status === 408 || status === 429 || status >= 500;
}

// Best-effort: drop an upload the sender no longer wants (file removed from
// the form, or rejected by the server) so it stops counting against their
// limit of unsent attachments. Never throws.
export function cancelUpload(uploadId) {
  if (!uploadId) return Promise.resolve();
  return api.delete(`/broadcasts/uploads/${encodeURIComponent(uploadId)}`).catch(() => {});
}

/**
 * Upload one file in 700 KB slices. Resolves to the server's upload_id, to be
 * passed in `upload_ids` when the broadcast is created.
 *   onProgress({ loaded, total, percent })  — optional, called as bytes go out
 */
export async function uploadInChunks(file, { onProgress } = {}) {
  const f = await shrinkImage(file);
  const total = f.size;
  const report = (loaded) => {
    if (!onProgress) return;
    const done = Math.min(total, Math.max(0, loaded));
    onProgress({ loaded: done, total, percent: total ? Math.round((done * 100) / total) : 100 });
  };

  const { data } = await api.post("/broadcasts/uploads", {
    name: f.name, size: total, content_type: contentTypeOf(f),
  });
  const uploadId = data.upload_id;
  const chunkSize = Number(data.chunk_size) || CHUNK_SIZE;
  const count = Math.ceil(total / chunkSize);
  report(0);

  for (let i = 0; i < count; i += 1) {
    const start = i * chunkSize;
    const slice = f.slice(start, Math.min(total, start + chunkSize));
    for (let attempt = 0; ; attempt += 1) {
      try {
        await api.put(`/broadcasts/uploads/${encodeURIComponent(uploadId)}/chunks/${i}`, slice, {
          headers: { "Content-Type": "application/octet-stream" },
          timeout: CHUNK_TIMEOUT_MS,
          onUploadProgress: (e) => report(start + Math.min(e.loaded || 0, slice.size)),
        });
        break;
      } catch (err) {
        if (attempt >= RETRIES || !isRetryable(err)) throw err;
        await sleep(1000 * 2 ** attempt); // 1 s, 2 s, 4 s
      }
    }
    report(start + slice.size);
  }
  return uploadId;
}
